import React from 'react';
import clsx from 'clsx';

const Table = ({ children, className, variant = 'default', ...props }) => {
  const baseClasses = 'min-w-full divide-y divide-gray-200';
  
  const variants = {
    default: '',
    striped: 'divide-gray-200',
    bordered: 'border border-gray-200'
  };

  const classes = clsx(
    baseClasses,
    variants[variant],
    className
  );

  return (
    <div className="overflow-x-auto" data-otg-component={props['data-otg-component'] || 'Table'}>
      <table className={classes} {...props}>
        {children}
      </table>
    </div>
  );
};

const TableHeader = ({ children, className, ...props }) => {
  const classes = clsx('bg-gray-50', className);
  
  return (
    <thead className={classes} {...props} data-otg-component={props['data-otg-component'] || 'TableHeader'}>
      {children}
    </thead>
  );
};

const TableBody = ({ children, className, striped = false, ...props }) => {
  const classes = clsx(
    'bg-white divide-y divide-gray-200',
    striped && 'divide-gray-200',
    className
  );
  
  return (
    <tbody className={classes} {...props} data-otg-component={props['data-otg-component'] || 'TableBody'}>
      {children}
    </tbody>
  );
};

const TableRow = ({ children, className, striped = false, hover = true, ...props }) => {
  const classes = clsx(
    striped && 'odd:bg-gray-50 even:bg-white',
    hover && 'hover:bg-gray-50',
    'transition-colors',
    className
  );
  
  return (
    <tr className={classes} {...props} data-otg-component={props['data-otg-component'] || 'TableRow'}>
      {children}
    </tr>
  );
};

const TableHead = ({ children, className, sortable = false, sorted, onSort, ...props }) => {
  const classes = clsx(
    'px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider',
    sortable && 'cursor-pointer select-none hover:bg-gray-100',
    className
  );

  const handleClick = () => {
    if (sortable && onSort) {
      onSort();
    }
  };

  return (
    <th 
      className={classes} 
      onClick={handleClick}
      {...props}
      data-otg-component={props['data-otg-component'] || 'TableHead'}
    >
      <div className="flex items-center space-x-1">
        <span>{children}</span>
        {sortable && (
          <span className="flex flex-col">
            <svg 
              className={clsx(
                'w-3 h-3',
                sorted === 'asc' ? 'text-gray-900' : 'text-gray-400'
              )}
              fill="currentColor" 
              viewBox="0 0 20 20"
            >
              <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
            </svg>
            <svg 
              className={clsx(
                'w-3 h-3 -mt-1',
                sorted === 'desc' ? 'text-gray-900' : 'text-gray-400'
              )}
              fill="currentColor" 
              viewBox="0 0 20 20"
            >
              <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </span>
        )}
      </div>
    </th>
  );
};

const TableCell = ({ children, className, align = 'left', ...props }) => {
  const alignClasses = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right'
  };

  const classes = clsx(
    'px-6 py-4 whitespace-nowrap text-sm text-gray-900',
    alignClasses[align],
    className
  );
  
  return (
    <td className={classes} {...props} data-otg-component={props['data-otg-component'] || 'TableCell'}>
      {children}
    </td>
  );
};

const TableFooter = ({ children, className, ...props }) => {
  const classes = clsx('bg-gray-50', className);
  
  return (
    <tfoot className={classes} {...props} data-otg-component={props['data-otg-component'] || 'TableFooter'}>
      {children}
    </tfoot>
  );
};

// Empty state component for tables
const TableEmpty = ({ children, className, ...props }) => {
  const classes = clsx(
    'text-center py-12 text-gray-500',
    className
  );

  return (
    <tbody data-otg-component={props['data-otg-component'] || 'TableEmpty'}>
      <tr>
        <td colSpan="100%" className={classes} {...props}>
          <div className="flex flex-col items-center space-y-2">
            <svg className="w-12 h-12 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <div>{children}</div>
          </div>
        </td>
      </tr>
    </tbody>
  );
};

Table.Header = TableHeader;
Table.Body = TableBody;
Table.Row = TableRow;
Table.Head = TableHead;
Table.Cell = TableCell;
Table.Footer = TableFooter;
Table.Empty = TableEmpty;

export default Table;
